import React, { useState, useEffect } from 'react';
import { adminAPI } from '../api/adminAPI';
import AdminLayout from '../components/AdminLayout';

const CommunicationLogsPage = () => {
  const [activeTab, setActiveTab] = useState('email');
  const [emailLogs, setEmailLogs] = useState([]);
  const [smsLogs, setSmsLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [activeTab, filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = { ...filters };
      
      if (activeTab === 'email') {
        const response = await adminAPI.get('/communications/emails', { params });
        setEmailLogs(response.data.emailLogs || []);
      } else {
        const response = await adminAPI.get('/communications/sms', { params });
        setSmsLogs(response.data.smsLogs || []);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await adminAPI.get('/communications/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSendSms = async () => {
    const recipient = prompt('Enter phone number:');
    if (!recipient) return;
    const message = prompt('Enter message:');
    if (!message) return;

    try {
      await adminAPI.post('/communications/sms/send', { recipient, message });
      alert('SMS sent successfully!');
      fetchData();
    } catch (error) {
      console.error('Error sending SMS:', error);
      alert('Failed to send SMS');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      sent: 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800',
      opened: 'bg-purple-100 text-purple-800',
      failed: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Communication Logs</h1>
          <p className="text-gray-600 mt-2">Track all email and SMS communications</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-600">Total Emails</p>
              <p className="text-2xl font-bold text-blue-600">{stats.emails?.total || 0}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-600">Email Delivered</p>
              <p className="text-2xl font-bold text-green-600">{stats.emails?.byStatus?.delivered || 0}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-600">Total SMS</p>
              <p className="text-2xl font-bold text-purple-600">{stats.sms?.total || 0}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <p className="text-sm text-gray-600">SMS Delivered</p>
              <p className="text-2xl font-bold text-green-600">{stats.sms?.byStatus?.delivered || 0}</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b">
            <div className="flex">
              <button
                onClick={() => setActiveTab('email')}
                className={`px-6 py-3 font-medium ${
                  activeTab === 'email'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Email Logs
              </button>
              <button
                onClick={() => setActiveTab('sms')}
                className={`px-6 py-3 font-medium ${
                  activeTab === 'sms'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                SMS Logs
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 border-b">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="px-3 py-2 border rounded-lg"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="sent">Sent</option>
                <option value="delivered">Delivered</option>
                <option value="opened">Opened</option>
                <option value="failed">Failed</option>
              </select>

              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                className="px-3 py-2 border rounded-lg"
              >
                <option value="">All Types</option>
                <option value="job_application">Job Application</option>
                <option value="contact_response">Contact Response</option>
                <option value="newsletter">Newsletter</option>
                <option value="notification">Notification</option>
              </select>

              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="px-3 py-2 border rounded-lg"
              />

              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="px-3 py-2 border rounded-lg"
              />
            </div>
            
            {activeTab === 'sms' && (
              <div className="mt-4">
                <button
                  onClick={handleSendSms}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Send SMS
                </button>
              </div>
            )}
          </div>

          {/* Logs Table */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recipient
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {activeTab === 'email' ? 'Subject' : 'Message'}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activeTab === 'email' && emailLogs.map(log => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{log.recipient}</td>
                      <td className="px-6 py-4 text-sm">{log.subject}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{log.type}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(log.status)}`}>
                          {log.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                  {activeTab === 'sms' && smsLogs.map(log => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{log.recipient}</td>
                      <td className="px-6 py-4 text-sm">{log.message}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{log.type}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(log.status)}`}>
                          {log.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CommunicationLogsPage;
